import React, { useEffect, useState } from "react";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import {
  Users,
  TrendingUp,
  Award,
  Copy,
  Check,
  AlertCircle,
  ChevronRight,
  X,
  Info,
  Target,
  Sparkles,
  Shield,
  DollarSign,
  Calendar,
  Phone,
  Mail,
  User,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink
} from "lucide-react";
import Modal from "react-modal";

const Network = () => {
  const { user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [team, setTeam] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);

  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);

  const openTeamModal = () => setIsTeamModalOpen(true);
  const closeTeamModal = () => setIsTeamModalOpen(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const debugRes = await api.get("/mlm/debug/referrals").catch(() => null);

      const [teamRes, rewardsRes] = await Promise.all([
        api.get("/mlm/team"),
        api.get("/mlm/rewards"),
      ]);
      
      setTeam(teamRes.data);
      setRewards(rewardsRes.data);

      if (debugRes) {
        setDebugInfo(debugRes.data);
      }

      try {
        const dashboardRes = await api.get("/mlm/dashboard");
        setSubscription(dashboardRes.data.subscription);
      } catch (err) {
        console.log("MLM not yet activated");
        setSubscription(null);
      }
    } catch (err) {
      console.error("MLM load failed", err);
      setError(err?.response?.data?.detail || "Failed to load MLM data");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateMLM = async () => {
    try {
      setActivating(true);
      setError(null);

      const payload = {};
      if (user?.activeDepositId) {
        payload.depositId = user.activeDepositId;
      }

      await api.post("/mlm/subscribe", payload);
      await loadData();
      
      alert("MLM activated successfully!");
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || "Activation failed";
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setActivating(false);
    }
  };

  const handleCopy = () => {
    const referralLink = `${window.location.origin}/register?ref=${user?.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </MobileLayout>
    );
  }

  const directCount = team?.activeDirectReferrals || 0;
  const totalDirectCount = team?.directReferrals || 0;
  const hasActiveDeposit = debugInfo?.userDeposits?.hasActiveDeposit || false;
  const canActivate = hasActiveDeposit && directCount >= 5;

  return (
    <MobileLayout>
      <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-6">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Referral Network</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Build your team & earn rewards</p>
        </div>

        {/* ERROR MESSAGE */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 md:p-4 rounded-xl flex gap-3 items-start">
            <AlertCircle className="flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {/* DEBUG INFO */}
        {debugInfo && process.env.NODE_ENV === "development" && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-xs md:text-sm">
            <div className="font-bold mb-2 flex items-center gap-2">
              <Info size={16} />
              <span>Debug Info</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>User ID: {debugInfo.userId}</div>
              <div>Total Referrals: {debugInfo.totalDirectReferrals}</div>
              <div>Active Referrals: {debugInfo.activeDirectReferrals}</div>
            </div>
          </div>
        )}

        {/* MLM ACTIVATION */}
        {!subscription && (
          <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg md:text-xl text-gray-900">Activate MLM Subscription</h3>
              <Shield className="text-primary" size={24} />
            </div>

            <div className="bg-gray-50 p-4 rounded-xl space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Active Deposit</span>
                <div className="flex items-center gap-2">
                  {hasActiveDeposit ? (
                    <CheckCircle className="text-green-600" size={18} />
                  ) : (
                    <XCircle className="text-red-600" size={18} />
                  )}
                  <span className={`font-semibold ${hasActiveDeposit ? "text-green-600" : "text-red-600"}`}>
                    {hasActiveDeposit ? "Available" : "Required"}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Direct Referrals</span>
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-gray-500" />
                  <span className="font-semibold">
                    {directCount} / 5
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Referrals</span>
                <span className="font-semibold">{totalDirectCount}</span>
              </div>
            </div>

            {!hasActiveDeposit && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="flex-shrink-0 mt-0.5 text-red-600" size={20} />
                  <div>
                    <div className="font-semibold text-red-800 mb-1">No Active Deposit</div>
                    <div className="text-sm text-red-700">
                      You need to have an active deposit before you can activate MLM.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {directCount < 5 && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
                <div className="flex items-start gap-3">
                  <Target className="flex-shrink-0 mt-0.5 text-yellow-600" size={20} />
                  <div>
                    <div className="font-semibold text-yellow-800 mb-2">Requirements</div>
                    <ul className="space-y-1 text-sm text-yellow-700">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                        <span>5 direct referrals with active deposits</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                        <span>Share your referral link to invite users</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></div>
                        <span>Each referral must have an active deposit</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <button
              disabled={!canActivate || activating}
              onClick={handleActivateMLM}
              className={`w-full py-3 md:py-4 rounded-xl font-bold text-white transition-all duration-200
                ${canActivate 
                  ? "bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary-dark shadow-md hover:shadow-lg" 
                  : "bg-gray-300 cursor-not-allowed"
                }`}
            >
              {activating ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Activating...</span>
                </div>
              ) : (
                "Activate MLM"
              )}
            </button>
          </div>
        )}

        {/* ACTIVE LEVEL */}
        {subscription && (
          <div className="bg-gradient-to-br from-primary to-primary-dark text-white p-5 md:p-6 rounded-2xl shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm opacity-90 mb-1">Current Level</div>
                <h2 className="text-2xl md:text-3xl font-bold mb-1">
                  {subscription.currentTitle}
                </h2>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="opacity-80" />
                  <div className="text-sm opacity-90">
                    {subscription.profitSharePercent}% Profit Share
                  </div>
                </div>
              </div>
              <Sparkles size={28} className="opacity-90" />
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex justify-between items-center">
                <div className="text-sm opacity-80">Progress</div>
                <div className="text-sm font-semibold">
                  Level {subscription.currentLevel} of 10
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STATS GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <StatCard
            icon={<Users className="text-primary" size={24} />}
            value={directCount}
            label="Active Direct"
            subLabel={`${totalDirectCount} total`}
          />
          <StatCard
            icon={<TrendingUp className="text-green-600" size={24} />}
            value={subscription?.totalTeamSize || 0}
            label="Team Size"
            onClick={openTeamModal}
            clickable={true}
          />
          <StatCard
            icon={<Award className="text-yellow-500" size={24} />}
            value={formatIndianCurrency(subscription?.totalTeamBusiness || 0)}
            label="Business"
          />
        </div>

        {/* REFERRAL LINK */}
        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ExternalLink size={18} />
            <span>Your Referral Link</span>
          </div>
          <div className="text-sm text-gray-500 mb-4">
            Share this link to invite new users to your network
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1 bg-gray-50 p-3 rounded-xl border border-gray-200 overflow-hidden">
              <code className="text-xs md:text-sm text-gray-700 break-all">
                {window.location.origin}/register?ref={user?.referralCode}
              </code>
            </div>
            <button
              onClick={handleCopy}
              className={`px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                copied 
                  ? "bg-green-600 text-white" 
                  : "bg-primary text-white hover:bg-primary-dark"
              }`}
              title="Copy link"
            >
              {copied ? (
                <>
                  <Check size={18} />
                  <span className="hidden sm:inline">Copied</span>
                </>
              ) : (
                <>
                  <Copy size={18} />
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* NEXT REWARD */}
        {subscription?.nextLevel && (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 md:p-5 rounded-2xl border border-purple-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-purple-900 flex items-center gap-2">
                <Target className="text-purple-600" size={20} />
                <span>Next Reward</span>
              </div>
              <ChevronRight className="text-purple-400" size={20} />
            </div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="font-bold text-lg md:text-xl text-purple-900">
                  {subscription.nextLevel.title}
                </div>
                <div className="text-sm text-purple-700 mt-1">
                  Level {subscription.nextLevel.level} • {subscription.nextLevel.profitSharePercent}% Profit Share
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-purple-600 mb-1">Progress</div>
                <div className="font-bold text-lg text-purple-900">
                  {subscription.totalTeamSize} / {subscription.nextLevel.requiredTeam}
                </div>
              </div>
            </div>
            <div className="mt-4 bg-white/50 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full transition-all duration-700 rounded-full"
                style={{
                  width: `${Math.min(
                    100,
                    (subscription.totalTeamSize / subscription.nextLevel.requiredTeam) * 100
                  )}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* REWARDS SECTION */}
        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="text-yellow-500" size={20} />
            <span>Milestone Rewards</span>
          </div>
          <div className="space-y-3">
            {rewards.map((reward) => (
              <div
                key={reward.level}
                className={`p-3 md:p-4 rounded-xl border transition-all duration-200 ${
                  subscription?.currentLevel >= reward.level
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                    : subscription?.currentLevel === reward.level - 1
                    ? "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden ${
                      subscription?.currentLevel >= reward.level ? "ring-2 ring-green-500" : ""
                    }`}>
                      <img
                        src={reward.imageUrl}
                        alt={reward.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                        subscription?.currentLevel >= reward.level
                          ? "bg-green-600 text-white"
                          : "bg-primary text-white"
                      }`}>
                        Level {reward.level}
                      </span>
                      {subscription?.currentLevel >= reward.level && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-semibold">
                          <CheckCircle size={14} />
                          <span>Achieved</span>
                        </span>
                      )}
                    </div>
                    <div className="font-bold text-gray-900 text-sm md:text-base mb-1">
                      {reward.title}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <DollarSign size={12} />
                        <span>{reward.profitSharePercent}% Profit Share</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Award size={12} />
                        <span>{reward.physicalReward}</span>
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <Users size={12} />
                      <span>Requires {reward.requiredTeam} team members</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TEAM MODAL */}
      <Modal
        isOpen={isTeamModalOpen}
        onRequestClose={closeTeamModal}
        ariaHideApp={false}
        className="bg-white rounded-2xl p-5 mx-4 my-8 md:mx-auto md:max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start md:items-center z-50 p-4"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="text-primary" size={24} />
            <span>Team Members</span>
          </h2>
          <button
            onClick={closeTeamModal}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto pr-2">
          {team?.teamMembers?.length > 0 ? (
            <div className="space-y-3">
              {team.teamMembers.map((member) => (
                <div
                  key={member.userId}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-white transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="text-primary" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <div className="font-semibold text-gray-900 truncate">
                          {member.name}
                        </div>
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                          L{member.mlmLevel}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail size={14} className="text-gray-400" />
                          <span className="truncate">{member.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={14} className="text-gray-400" />
                          <a
                            href={`tel:${member.phone}`}
                            className="text-primary hover:underline truncate"
                          >
                            {member.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Calendar size={14} className="text-gray-400" />
                          <span>
                            Joined {new Date(member.joinedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users size={48} className="mx-auto text-gray-300 mb-3" />
              <div className="text-gray-500">No team members yet</div>
              <div className="text-sm text-gray-400 mt-1">
                Share your referral link to build your team
              </div>
            </div>
          )}
        </div>
        
        <button
          onClick={closeTeamModal}
          className="mt-4 w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
        >
          Close
        </button>
      </Modal>
    </MobileLayout>
  );
};

const StatCard = ({ icon, value, label, subLabel, onClick, clickable = false }) => (
  <div
    onClick={onClick}
    className={`bg-white p-4 rounded-xl border border-gray-200 text-center cursor-default hover:shadow-md transition-all duration-200 ${
      clickable ? "cursor-pointer hover:border-primary" : ""
    }`}
  >
    <div className="flex justify-center mb-2">{icon}</div>
    <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
    <div className="text-sm font-medium text-gray-700">{label}</div>
    {subLabel && (
      <div className="text-xs text-gray-400 mt-1">{subLabel}</div>
    )}
  </div>
);

export default Network;